import { useState, useEffect, useCallback, useRef } from 'react';
import GooglePayButton from '@google-pay/button-react';
import { useCheckout } from '@/contexts/checkoutContext';
import {
  convertGooglePayAddress,
  convertGooglePayBillingAddress,
} from '@/utils/addressConverter';
import { buildScaData } from '@/utils/buildScaData';
import {
  parsePrice,
  calculateBaseAmount,
  buildDisplayItems,
} from '@/utils/googlePayPricing';
import { useGooglePayDeliveryOptions } from '@/hooks/useGooglePayDeliveryOptions';
import { useGooglePay } from '@/hooks/useGooglePay';
import { useGooglePayBuyNow } from '@/hooks/useGooglePayBuyNow';
import { useThreeDSecureCompletion } from '@/hooks/useThreeDSecureCompletion';
import { handleCouponCode } from '@/hooks/couponHandler';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ThreeDSecureModal } from '@/components/ThreeDSecureModal';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { Endpoint } from '@/constants';
import { getAntiForgeryToken } from '@/utils/getAntiForgeryToken';
import type {
  GooglePayOrder,
  GooglePayPaymentData,
  GooglePayDeliveryOptionsResponse,
  ExtendedCheckoutData,
} from '@/types/googlePay';
import type { CheckoutData } from '@/types';
import * as Styled from './GooglePay.styled';

interface GooglePayButtonWrapperProps {
  style: string;
  buttonId: string;
  googlePaySettings: import('@/types/quickPay').GooglePaySettings;
  isBuyNow?: boolean;
  productCode?: string;
  quantity?: number;
  productPrice?: string; // Current (potentially discounted) price for Buy Now mode
  productOriginalPrice?: string | null; // Original price before discount for Buy Now mode
}

// Global flags to track PaymentRequest state
let globalPaymentRequestActive = false;
let globalProcessingActive = false;

export const GooglePayButtonWrapper = ({
  style: _style,
  buttonId: _buttonId,
  googlePaySettings,
  isBuyNow = false,
  productCode,
  quantity = 1,
  productPrice,
  productOriginalPrice,
}: GooglePayButtonWrapperProps) => {
  console.log('GooglePayButtonWrapper: Component mounted with props:', {
    style: _style,
    buttonId: _buttonId,
    googlePaySettings: !!googlePaySettings,
    isBuyNow,
    productCode,
    quantity,
    productPrice,
    productOriginalPrice,
  });

  const [paymentRequest, setPaymentRequest] =
    useState<google.payments.api.PaymentDataRequest | null>(null);
  const currentShippingOptionsRef = useRef<any[]>([]);
  const currentCountryRef = useRef<string>('IE');
  const currentCouponCodeRef = useRef<string | null>(null);
  const [shippingOptionPrices, setShippingOptionPrices] = useState<
    Record<string, number>
  >({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [threeDSecureUrl, setThreeDSecureUrl] = useState<string | null>(null);
  const [currentTransactionId, setCurrentTransactionId] = useState<
    string | null
  >(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const componentMountedRef = useRef<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const previousCouponCodeRef = useRef<string | null>(null);
  const buyNowCartCreatedRef = useRef<boolean>(false);
  const cachedDeliveryOptionsRef = useRef<GooglePayDeliveryOptionsResponse | null>(null);

  // Conditionally access checkout context - only for regular checkout, not Buy Now
  const checkoutContext = !isBuyNow ? useCheckout() : null;

  // Get checkout data and refreshing state
  const checkoutData = checkoutContext?.checkoutData || null;
  const isRefreshing = checkoutContext?.isRefreshing || false;

  const { fetchDeliveryOptions } = useGooglePayDeliveryOptions();

  // Use different hooks based on whether this is Buy Now or regular checkout
  const { createGooglePayOrder, isPending: isRegularPending } = useGooglePay({
    onThreeDSecureRequired: url => {
      console.log('GooglePay: 3D Secure required, showing modal');
      setThreeDSecureUrl(url);
      // Extract transaction ID from URL if needed
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const transactionId =
        urlParams.get('transactionId') || urlParams.get('MD') || '';
      setCurrentTransactionId(transactionId);
    },
    isBuyNow: isBuyNow,
  });

  const { createBuyNowGooglePayOrder, isPending: isBuyNowPending } =
    useGooglePayBuyNow({
      onThreeDSecureRequired: url => {
        console.log('GooglePay Buy Now: 3D Secure required, showing modal');
        setThreeDSecureUrl(url);
        // Extract transaction ID from URL if needed
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const transactionId =
          urlParams.get('transactionId') || urlParams.get('MD') || '';
        setCurrentTransactionId(transactionId);
      },
      productCode: productCode || '',
      quantity: quantity,
    });

  const isPending = isBuyNow ? isBuyNowPending : isRegularPending;
  const { completeThreeDSecure, isPending: isCompletingThreeDSecure } =
    useThreeDSecureCompletion({
      isBuyNow: isBuyNow,
    });

  // Helper function to get amount based on mode
  const getAmount = (): number => {
    if (isBuyNow && productPrice) {
      return parsePrice(productPrice);
    }

    // Use checkout data for regular checkout
    if (checkoutData?.amounts?.total) {
      return parsePrice(checkoutData.amounts.total);
    }

    return 0;
  };

  // Cast checkoutData to extended type for additional properties
  const extendedCheckoutData = checkoutData as CheckoutData &
    ExtendedCheckoutData;

  // Helper function to set shipping method in cart and return updated cart
  const setShippingMethodInCart = async (
    shippingMethodName: string,
    cartNameParam?: string,
  ): Promise<any | null> => {
    try {
      console.log(
        'GooglePay: Setting shipping method in cart:',
        shippingMethodName,
        cartNameParam ? `(cart: ${cartNameParam})` : '',
      );

      const requestBody: { shippingMethodName: string; cartName?: string } = {
        shippingMethodName,
      };
      if (cartNameParam) {
        requestBody.cartName = cartNameParam;
      }

      const response = await fetch(Endpoint.SetShippingMethod, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          RequestVerificationToken: getAntiForgeryToken(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.warn(
          `GooglePay: Failed to set shipping method (HTTP ${response.status}):`,
          shippingMethodName,
        );
        return null;
      }

      const result = await response.json();
      if (result.isSuccess === false) {
        console.warn(
          'GooglePay: Shipping method update returned error:',
          result.messages,
        );
        return null;
      }

      console.log(
        'GooglePay: Shipping method updated successfully:',
        result.data,
      );
      return result.data; // Return the updated shopping cart
    } catch (error) {
      console.error('GooglePay: Error setting shipping method:', error);
      return null;
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    
    return () => {
      componentMountedRef.current = false;
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      // Clear the global flags when component unmounts
      if (isInitializingRef.current) {
        globalPaymentRequestActive = false;
        isInitializingRef.current = false;
      }
      globalProcessingActive = false;
      buyNowCartCreatedRef.current = false;
    };
  }, []);

  // Handle 3D Secure completion
  const handleThreeDSecureComplete = useCallback(
    (success: boolean) => {
      console.log('GooglePay: 3D Secure completed', { success });

      // Prevent duplicate 3DS completion calls
      if (isCompletingThreeDSecure) {
        console.warn('GooglePay: 3DS completion already in progress, ignoring duplicate call');
        return;
      }

      if (success && currentTransactionId) {
        // Clear only 3DS modal states
        setThreeDSecureUrl(null);
        setCurrentTransactionId(null);

        // Ensure loading overlay is visible while the final order is being created
        setIsProcessingPayment(true);

        // Reset/extend timeout so the overlay persists during the second API call
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
          console.warn(
            'GooglePay: Post-3DS processing timeout reached, clearing loading state',
          );
          setIsProcessingPayment(false);
        }, 30000);

        // Complete the order after successful 3DS
        completeThreeDSecure(
          {
            transactionId: currentTransactionId,
          },
          {
            onError: () => {
              // Clear loading state only on error
              console.log('GooglePay: 3DS order completion failed');
              globalProcessingActive = false;
              setIsProcessingPayment(false);
              if (processingTimeoutRef.current) {
                clearTimeout(processingTimeoutRef.current);
              }
            },
            // Note: We don't use onSettled to keep loading state active until redirect
          },
        );
      } else {
        // Clear all states on failure or user cancellation
        setThreeDSecureUrl(null);
        setCurrentTransactionId(null);
        setIsProcessingPayment(false);
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        if (!success) {
          NotificationsQueue.add(
            {
              notification: (
                <NotificationMessage $isError={true}>
                  Payment authentication failed. Please try again.
                </NotificationMessage>
              ),
            },
            { timeout: 5000 },
          );
        }
      }
    },
    [currentTransactionId, completeThreeDSecure],
  );

  // Enhanced validation with debugging
  if (!googlePaySettings) {
    console.error('GooglePay: No settings provided');
    return <div style={{ display: 'none' }}>Google Pay: No settings</div>;
  }

  // Debug log the settings
  console.log('GooglePay: Settings received:', googlePaySettings);

  // Enhanced debugging - log each property individually
  console.log('GooglePay: Property breakdown:', {
    enabled: googlePaySettings.enabled,
    environment: googlePaySettings.environment,
    merchantId: googlePaySettings.merchantId,
    merchantName: googlePaySettings.merchantName,
    gatewayName: googlePaySettings.gatewayName,
    countryCode: googlePaySettings.countryCode,
    currencyCode: googlePaySettings.currencyCode,
    // Check if these are the actual values
    merchantIdValue: `"${googlePaySettings.merchantId}"`,
    gatewayNameValue: `"${googlePaySettings.gatewayName}"`,
    isTestEnvironment: googlePaySettings.environment === 'TEST',
  });

  // Validate required settings
  if (!googlePaySettings.enabled) {
    console.log('GooglePay: Disabled via settings');
    return <div style={{ display: 'none' }}>Google Pay: Disabled</div>;
  }

  if (!googlePaySettings.merchantId || !googlePaySettings.merchantName) {
    console.error('GooglePay: Missing required merchant configuration', {
      merchantId: googlePaySettings.merchantId,
      merchantName: googlePaySettings.merchantName,
      gatewayName: googlePaySettings.gatewayName,
      merchantIdType: typeof googlePaySettings.merchantId,
      merchantNameType: typeof googlePaySettings.merchantName,
      gatewayNameType: typeof googlePaySettings.gatewayName,
    });
    return (
      <div style={{ display: 'none' }}>Google Pay: Missing merchant config</div>
    );
  }

  // Warn if gatewayName is missing but continue with fallback
  if (!googlePaySettings.gatewayName) {
    console.warn(
      'GooglePay: gatewayName is missing, using fallback "opayoelavon"',
      {
        gatewayName: googlePaySettings.gatewayName,
        gatewayNameType: typeof googlePaySettings.gatewayName,
      },
    );
  }

  const countryCode = googlePaySettings.countryCode || 'IE';

  // Handle shipping address and option changes
  const onPaymentDataChanged = useCallback(
    async (
      intermediatePaymentData: google.payments.api.IntermediatePaymentData,
    ): Promise<google.payments.api.PaymentDataRequestUpdate> => {
      console.log(
        'GooglePay: onPaymentDataChanged called',
        intermediatePaymentData,
      );

      // Get current amount based on mode
      let currentAmount = 0;
      if (isBuyNow) {
        currentAmount = getAmount();
      } else if (checkoutData?.amounts) {
        currentAmount = calculateBaseAmount(checkoutData.amounts);
      }

      console.log('GooglePay: Current amount:', {
        isBuyNow,
        currentAmount,
        source: isBuyNow ? 'product' : 'checkout',
        checkoutAmounts: checkoutData?.amounts,
        total: checkoutData?.amounts?.total,
        subtotal: checkoutData?.amounts?.subtotal,
        discount: checkoutData?.amounts?.discount,
        shipping: checkoutData?.amounts?.shipping,
      });

      try {
        const response: google.payments.api.PaymentDataRequestUpdate = {};

        // Update country ref early if address changed
        // This ensures coupon handler gets the correct country
        if (intermediatePaymentData.shippingAddress) {
          const newCountryCode = intermediatePaymentData.shippingAddress.countryCode || 'IE';
          const previousCountry = currentCountryRef.current;
          currentCountryRef.current = newCountryCode;
          console.log('GooglePay: Country ref updated from', previousCountry, 'to', newCountryCode);
        }

        // Pre-create Buy Now cart on first interaction so backend calls work
        if (isBuyNow && !buyNowCartCreatedRef.current && intermediatePaymentData.shippingAddress) {
          console.log('GooglePay: Creating Buy Now cart for backend pricing');
          const buyNowCartName = `GooglePay_BuyNow_${productCode}`;

          try {
            const addResponse = await fetch(Endpoint.AddToBuyNowCart, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': getAntiForgeryToken(),
              },
              body: JSON.stringify({
                code: productCode,
                quantity: quantity,
                cartName: buyNowCartName,
              }),
            });

            if (addResponse.ok) {
              buyNowCartCreatedRef.current = true;
              console.log('GooglePay: Buy Now cart created successfully');
            } else {
              console.warn('GooglePay: Failed to create Buy Now cart, falling back to local pricing');
            }
          } catch (cartError) {
            console.warn('GooglePay: Error creating Buy Now cart:', cartError);
          }
        }

        // Handle coupon/offer code changes - check for multiple codes
        const redemptionCodes = intermediatePaymentData.offerData?.redemptionCodes || [];
        console.log('GooglePay: Received redemption codes:', redemptionCodes);

        // Check if user is trying to add multiple codes
        if (redemptionCodes.length > 1) {
          console.warn('GooglePay: Multiple redemption codes detected, only one allowed');

          // Keep the first code (the existing valid one) and reject the attempt to add a second
          const existingCode = redemptionCodes[0];

          // Return error with the existing code preserved in newOfferInfo
          return {
            error: {
              reason: 'OFFER_INVALID' as google.payments.api.ErrorReason,
              message: 'Only one promo code can be applied at a time. Please remove the existing code first.',
              intent: 'OFFER' as google.payments.api.CallbackIntent,
            },
            newOfferInfo: {
              offers: [{
                redemptionCode: existingCode,
                description: 'Applied code',
              }]
            },
          };
        }

        const currentCouponCode = redemptionCodes[0] || '';
        const couponCodeChanged = currentCouponCode !== (previousCouponCodeRef.current || '');

        if (intermediatePaymentData.callbackTrigger === 'OFFER' && couponCodeChanged) {
          console.log('GooglePay: Coupon code changed from', previousCouponCodeRef.current, 'to', currentCouponCode);

          // Call the coupon handler with the updated country
          const couponResult = await handleCouponCode(
            currentCouponCode,
            isBuyNow ? `GooglePay_BuyNow_${productCode}` : undefined,
            currentCountryRef.current
          );

          if (!couponResult.success) {
            // Don't update the ref - keep the previous valid code
            // Return error to Google Pay - do NOT include newOfferInfo as it causes duplicates
            return {
              error: {
                reason: 'OFFER_INVALID' as google.payments.api.ErrorReason,
                message: couponResult.errors?.[0]?.message || 'Invalid offer code',
                intent: 'OFFER' as google.payments.api.CallbackIntent,
              },
            };
          }

          // Only update the ref after successful validation
          previousCouponCodeRef.current = currentCouponCode;

          // Store the coupon code for display
          currentCouponCodeRef.current = currentCouponCode || null;

          // Update transaction info with new totals from cart
          if (couponResult.cartData) {
            const updatedCart = couponResult.cartData;
            const backendTotal = updatedCart.amounts?.totalDecimal || 0;
            const backendSubtotal = updatedCart.amounts?.subtotalDecimal || 0;
            const backendDiscount = updatedCart.amounts?.discountDecimal || 0;
            const backendCouponDiscount = updatedCart.amounts?.couponDiscountDecimal || 0;
            const backendShipping = updatedCart.amounts?.shippingDecimal || 0;

            // Build display items with coupon applied
            const displayItems: google.payments.api.DisplayItem[] = [];

            if (backendSubtotal > 0) {
              displayItems.push({
                label: 'Subtotal',
                type: 'SUBTOTAL' as google.payments.api.DisplayItemType,
                price: backendSubtotal.toFixed(2),
              });
            }

            // Add regular discount first
            const roundedDiscount = parseFloat(backendDiscount.toFixed(2));
            if (roundedDiscount > 0) {
              displayItems.push({
                label: 'Discount',
                type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
                price: `-${roundedDiscount.toFixed(2)}`,
              });
            }

            // Add coupon discount second
            const roundedCouponDiscount = parseFloat(backendCouponDiscount.toFixed(2));
            if (roundedCouponDiscount > 0) {
              const couponCode = updatedCart.discountCode || 'COUPON';
              displayItems.push({
                label: couponCode,
                type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
                price: `-${roundedCouponDiscount.toFixed(2)}`,
              });
            }

            if (backendShipping > 0) {
              displayItems.push({
                label: 'Shipping',
                type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
                price: backendShipping.toFixed(2),
              });
            }

            response.newTransactionInfo = {
              totalPriceStatus: 'FINAL',
              totalPriceLabel: 'Total',
              totalPrice: backendTotal.toFixed(2),
              currencyCode: googlePaySettings.currencyCode || 'EUR',
              countryCode: currentCountryRef.current,
              displayItems: displayItems,
            };

            // Update shipping methods if returned
            if (couponResult.quickPayShippingMethods) {
              response.newShippingOptionParameters = {
                shippingOptions: couponResult.quickPayShippingMethods.map(opt => ({
                  id: opt.identifier,
                  label: opt.label,
                  description: opt.detail,
                })),
              };
            }
          }

          // If only coupon changed (no country/shipping change), return now
          if (!intermediatePaymentData.shippingAddress && !intermediatePaymentData.shippingOptionData) {
            console.log('GooglePay: Only coupon changed, returning response:', response.newTransactionInfo);
            return response;
          }

          // Otherwise continue to process country/shipping changes below
          console.log('GooglePay: Coupon processed, continuing to handle country/shipping changes');
        }

        // Handle shipping address change
        if (intermediatePaymentData.shippingAddress) {
          const countryCode = currentCountryRef.current; // Already updated above
          const previousCountry = intermediatePaymentData.shippingAddress.countryCode !== countryCode ?
            intermediatePaymentData.shippingAddress.countryCode : null;
          
          console.log(
            'GooglePay: Shipping address changed to country:',
            countryCode,
            'from:',
            previousCountry,
          );

          // Fetch delivery options for the new country using GetShippingOptions endpoint
          const deliveryOptionsResponse =
            await fetchDeliveryOptions(countryCode, isBuyNow, productCode);
          console.log(
            'GooglePay: Delivery options response:',
            deliveryOptionsResponse,
          );

          if (!deliveryOptionsResponse.isDeliverySupported) {
            // Country not supported for delivery by backend
            return {
              error: {
                reason:
                  'SHIPPING_ADDRESS_UNSERVICEABLE' as google.payments.api.ErrorReason,
                message:
                  deliveryOptionsResponse.unsupportedMessage ||
                  'We currently do not deliver to this country.',
                intent:
                  'SHIPPING_ADDRESS' as google.payments.api.CallbackIntent,
              },
            };
          }

          if (
            !deliveryOptionsResponse.options ||
            deliveryOptionsResponse.options.length === 0
          ) {
            // No delivery options available
            return {
              error: {
                reason:
                  'SHIPPING_ADDRESS_UNSERVICEABLE' as google.payments.api.ErrorReason,
                message:
                  'No delivery options available for the selected country.',
                intent:
                  'SHIPPING_ADDRESS' as google.payments.api.CallbackIntent,
              },
            };
          }

          // Convert options to Google Pay format - NO amount field in initial options
          const shippingOptions = deliveryOptionsResponse.options.map(
            option => ({
              id: option.id,
              label: option.label, // Use title as-is from backend
              description: option.description, // Already includes price from backend
            }),
          );

          // Find default option or use first one
          const defaultOption =
            deliveryOptionsResponse.options.find(opt => opt.isDefault) ||
            deliveryOptionsResponse.options[0];

          response.newShippingOptionParameters = {
            defaultSelectedOptionId: defaultOption?.id,
            shippingOptions: shippingOptions,
          };

          // Update current shipping options and store prices
          currentShippingOptionsRef.current = deliveryOptionsResponse.options;
          const prices: Record<string, number> = {};
          deliveryOptionsResponse.options.forEach(opt => {
            prices[opt.id] = parsePrice(opt.amount);
          });
          setShippingOptionPrices(prices);
          
          // Set the default shipping method in cart and update transaction total
          // so the displayed price reflects the new country's default shipping cost immediately.
          // Skip if this callback also contains a shipping option selection (handled below).
          if (defaultOption && !intermediatePaymentData.shippingOptionData?.id) {
            const cartNameParam = isBuyNow ? `GooglePay_BuyNow_${productCode}` : undefined;
            console.log('GooglePay: Setting default shipping method in cart for new country');
            const updatedCart = await setShippingMethodInCart(defaultOption.id, cartNameParam);
            if (updatedCart) {
              console.log('GooglePay: Default shipping method set for country:', countryCode);
              // Update transaction info with backend-calculated totals so the
              // Google Pay sheet shows the correct price for the new country immediately
              const backendTotal = updatedCart.amounts?.totalDecimal || 0;
              const backendSubtotal = updatedCart.amounts?.subtotalDecimal || 0;
              const backendShipping = updatedCart.amounts?.shippingDecimal || 0;
              const backendDiscount = updatedCart.amounts?.discountDecimal || 0;

              const displayItems: google.payments.api.DisplayItem[] = [];
              if (backendSubtotal > 0) {
                displayItems.push({
                  label: 'Subtotal',
                  type: 'SUBTOTAL' as google.payments.api.DisplayItemType,
                  price: backendSubtotal.toFixed(2),
                });
              }
              const roundedDiscount = parseFloat(backendDiscount.toFixed(2));
              if (roundedDiscount > 0) {
                displayItems.push({
                  label: 'Discount',
                  type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
                  price: `-${roundedDiscount.toFixed(2)}`,
                });
              }
              displayItems.push({
                label: 'Shipping',
                type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
                price: backendShipping.toFixed(2),
              });

              response.newTransactionInfo = {
                totalPriceStatus: 'FINAL',
                totalPriceLabel: 'Total',
                totalPrice: backendTotal.toFixed(2),
                currencyCode: googlePaySettings.currencyCode || 'EUR',
                countryCode: currentCountryRef.current,
                displayItems: displayItems,
              };
            }
          }
        }

        // Handle shipping option selection
        if (intermediatePaymentData.shippingOptionData?.id) {
          console.log(
            'GooglePay: Shipping option selected:',
            intermediatePaymentData.shippingOptionData.id,
            'for country:',
            currentCountryRef.current,
          );

          // If no subtotal is available yet, return without updating
          if (currentAmount <= 0) {
            console.warn(
              'GooglePay: No subtotal available yet, skipping price update',
            );
            return response;
          }

          if (!currentShippingOptionsRef.current || currentShippingOptionsRef.current.length === 0) {
            console.error(
              'GooglePay: No shipping options available for selected option',
            );
            return {
              error: {
                reason:
                  'SHIPPING_OPTION_INVALID' as google.payments.api.ErrorReason,
                message: 'Invalid shipping option selected',
                intent: 'SHIPPING_OPTION' as google.payments.api.CallbackIntent,
              },
            };
          }

          const requestedOptionId = intermediatePaymentData.shippingOptionData?.id;
          // Google Pay sends 'shipping_option_unselected' as a placeholder on initial
          // sheet open before the user has picked anything. Treat it as "no selection".
          const isPlaceholder = !requestedOptionId || requestedOptionId === 'shipping_option_unselected';

          let selectedOption = isPlaceholder
            ? undefined
            : currentShippingOptionsRef.current.find(opt => opt.id === requestedOptionId);

          if (!selectedOption) {
            if (!isPlaceholder) {
              // Only warn for real IDs that don't match (country-switch carryover)
              console.warn(
                'GooglePay: Selected shipping option not found:',
                requestedOptionId,
                '— Available options for',
                currentCountryRef.current,
                ':',
                currentShippingOptionsRef.current.map(opt => opt.id),
              );
            } else {
              console.log('GooglePay: No shipping option selected yet, using default for', currentCountryRef.current);
            }

            // Use the default shipping option for the current country
            const defaultOption = currentShippingOptionsRef.current.find(opt => opt.isDefault) ||
                                currentShippingOptionsRef.current[0];

            if (defaultOption) {
              console.log(
                'GooglePay: Using default shipping option:',
                defaultOption.id,
                'for country:',
                currentCountryRef.current,
              );
              selectedOption = defaultOption;
              
              // Tell Google Pay to update to the default option
              response.newShippingOptionParameters = {
                defaultSelectedOptionId: defaultOption.id,
                shippingOptions: currentShippingOptionsRef.current.map(option => ({
                  id: option.id,
                  label: option.label,
                  description: option.description,
                })),
              };
            } else {
              // No shipping options available at all
              console.error('GooglePay: No shipping options available for country:', currentCountryRef.current);
              return {
                error: {
                  reason:
                    'SHIPPING_OPTION_INVALID' as google.payments.api.ErrorReason,
                  message: 'No shipping options available',
                  intent: 'SHIPPING_OPTION' as google.payments.api.CallbackIntent,
                },
              };
            }
          }

          console.log('GooglePay: Selected shipping option:', selectedOption);

          // Set shipping method in cart and get updated totals from backend
          const cartNameParam = isBuyNow ? `GooglePay_BuyNow_${productCode}` : undefined;
          const updatedCart = await setShippingMethodInCart(selectedOption.id, cartNameParam);

          if (!updatedCart) {
            console.error('GooglePay: Failed to update shipping method in cart');
            return {
              error: {
                reason: 'OTHER_ERROR' as google.payments.api.ErrorReason,
                message: 'Failed to update shipping method',
                intent: 'SHIPPING_OPTION' as google.payments.api.CallbackIntent,
              },
            };
          }

          // Sync discount code from backend cart into refs and offer display
          if (updatedCart.discountCode) {
            previousCouponCodeRef.current = updatedCart.discountCode;
            currentCouponCodeRef.current = updatedCart.discountCode;
            response.newOfferInfo = {
              offers: [{
                redemptionCode: updatedCart.discountCode,
                description: 'Applied code',
              }]
            };
            console.log('GooglePay: Synced discount code from backend:', updatedCart.discountCode);
          }

          // Use backend-calculated totals from decimal properties
          const backendTotal = updatedCart.amounts?.totalDecimal || 0;
          const backendSubtotal = updatedCart.amounts?.subtotalDecimal || 0;
          const backendShipping = updatedCart.amounts?.shippingDecimal || 0;
          const backendTax = updatedCart.amounts?.taxDecimal || 0;
          const backendDiscount = updatedCart.amounts?.discountDecimal || 0;
          const backendCouponDiscount = updatedCart.amounts?.couponDiscountDecimal || 0;

          console.log('GooglePay: Using backend-calculated totals:', {
            subtotal: backendSubtotal,
            shipping: backendShipping,
            tax: backendTax,
            discount: backendDiscount,
            couponDiscount: backendCouponDiscount,
            total: backendTotal,
          });

          // Build display items with backend data
          const displayItems: google.payments.api.DisplayItem[] = [];

          // Add subtotal
          if (backendSubtotal > 0) {
            displayItems.push({
              label: 'Subtotal',
              type: 'SUBTOTAL' as google.payments.api.DisplayItemType,
              price: backendSubtotal.toFixed(2),
            });
          }

          // Add regular discount first
          const roundedDiscount = parseFloat(backendDiscount.toFixed(2));
          if (roundedDiscount > 0) {
            displayItems.push({
              label: 'Discount',
              type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
              price: `-${roundedDiscount.toFixed(2)}`,
            });
          }
 
          // Add shipping
          displayItems.push({
            label: 'Shipping',
            type: 'LINE_ITEM' as google.payments.api.DisplayItemType,
            price: backendShipping.toFixed(2),
          });
          
          response.newTransactionInfo = {
            totalPriceStatus: 'FINAL',
            totalPriceLabel: 'Total',
            totalPrice: backendTotal.toFixed(2),
            currencyCode: googlePaySettings.currencyCode || 'EUR',
            countryCode:
              intermediatePaymentData.shippingAddress?.countryCode ||
              countryCode,
            displayItems: displayItems,
          };

          // Tell Google Pay which shipping option is now selected
          response.newShippingOptionParameters = {
            defaultSelectedOptionId: selectedOption.id,
            shippingOptions: currentShippingOptionsRef.current.map(option => ({
              id: option.id,
              label: option.label,
              description: option.description,
            })),
          };
        }

        return response;
      } catch (error) {
        console.error('GooglePay: Error in onPaymentDataChanged', error);
        return {
          error: {
            reason: 'OTHER_ERROR' as google.payments.api.ErrorReason,
            message: 'Failed to update shipping options',
            intent: 'SHIPPING_OPTION' as google.payments.api.CallbackIntent,
          },
        };
      }
    },
    [
      shippingOptionPrices,
      checkoutData,
      countryCode,
      fetchDeliveryOptions,
      googlePaySettings.currencyCode,
      isBuyNow,
      productPrice,
      productOriginalPrice,
    ],
  );

  const handleGooglePayError = (error: any) => {
    // Handle errors from Google Pay
    console.error('Google Pay Error:', error);
    
    // Check if this is an initialization/configuration error
    // These errors typically occur during button setup, not during payment
    const isInitializationError = 
      !hasInitialized ||
      !isProcessingPayment && 
      !isPending && 
      (error?.statusCode === 'CANCELED' || 
       error?.statusCode === 'DEVELOPER_ERROR' ||
       error?.statusCode === 'MERCHANT_ACCOUNT_ERROR' ||
       !paymentRequest);
    
    if (isInitializationError) {
      console.warn('Google Pay initialization error - not showing user notification', error);
      // Don't show error notification for initialization errors
      // The button simply won't be available
      return;
    }
    
    // Only show error notification for actual payment errors
    NotificationsQueue.add(
      {
        notification: (
          <NotificationMessage $isError={true}>
            Payment failed. Please try again or use a different payment method.
          </NotificationMessage>
        ),
      },
      { timeout: 5000 },
    );
  };

  const handleGooglePaySuccess = async (paymentData: GooglePayPaymentData) => {
    console.log('GooglePay: Payment data received, starting order creation...');

    // Prevent duplicate order creation calls (including global check)
    if (isProcessingPayment || isPending || isCompletingThreeDSecure || globalProcessingActive) {
      console.warn('GooglePay: Order creation already in progress, ignoring duplicate call');
      return;
    }

    // Set global and local processing flags
    globalProcessingActive = true;
    setIsProcessingPayment(true);

    // Set a timeout to clear stuck loading states 
    // Extended for debugging sessions (5 minutes in development, 30 seconds in production)
    const timeoutDuration = process.env.NODE_ENV === 'development' ? 300000 : 30000;
    processingTimeoutRef.current = setTimeout(() => {
      console.warn(
        'GooglePay: Processing timeout reached, clearing loading state',
      );
      globalProcessingActive = false;
      setIsProcessingPayment(false);
    }, timeoutDuration);

    try {
      // Extract billing address from Google Pay response
      const billingAddress = paymentData.paymentMethodData.info.billingAddress;
      if (!billingAddress) {
        throw new Error('Billing address is required');
      }

      // Convert Google Pay billing address to Easons format with fallbacks
      const easonsAddress = convertGooglePayBillingAddress(billingAddress);
      // Note: No validation needed as convertGooglePayBillingAddress guarantees all required fields

      // For Buy Now, we don't have stored checkout data, so use minimal defaults
      const sourceCheckoutData = isBuyNow ? null : extendedCheckoutData;

      // Extract shipping address if available
      let shippingAddress = sourceCheckoutData?.shippingAddress;
      if (paymentData.shippingAddress) {
        shippingAddress = convertGooglePayAddress(paymentData.shippingAddress);
      }

      // Extract and validate selected shipping option against current country's options.
      // Google Pay may carry a stale option ID from a previous country selection;
      // if it doesn't match any option for the current country, fall back to the default.
      let selectedShippingOptionId = paymentData.shippingOptionData?.id;
      if (selectedShippingOptionId && currentShippingOptionsRef.current.length > 0) {
        const validOption = currentShippingOptionsRef.current.find(
          opt => opt.id === selectedShippingOptionId
        );
        if (!validOption) {
          console.warn(
            'GooglePay: Stale shipping option at submission:',
            selectedShippingOptionId,
            '— falling back to default for country:',
            currentCountryRef.current,
          );
          const defaultOpt = currentShippingOptionsRef.current.find(opt => opt.isDefault)
            || currentShippingOptionsRef.current[0];
          selectedShippingOptionId = defaultOpt?.id;
        }
      }

      // Build the Google Pay order
      const googlePayOrder: GooglePayOrder = {
        // Billing address from Google Pay
        BillingAddress: easonsAddress,

        // Shipping address from Google Pay or checkout context
        ShippingAddress: shippingAddress || undefined,

        // Email from Google Pay or checkout context
        Email: paymentData.email || sourceCheckoutData?.email || '',

        // Phone from billing address
        Phone: easonsAddress.Phone,

        // Guest status from checkout context (default to guest for Buy Now)
        IsGuest: sourceCheckoutData?.isGuest ?? true,

        // Only ship is available for Google Pay
        DeliveryOption: 'Ship',

        // Google Pay specific fields
        PaymentToken: paymentData.paymentMethodData.tokenizationData.token,
        ScaData: buildScaData(),
        CardNetwork: paymentData.paymentMethodData.info.cardNetwork,
        CardLastFour: paymentData.paymentMethodData.info.cardDetails,

        // Shipping option (validated above)
        ShippingOptionId: selectedShippingOptionId,
        ShippingCost: selectedShippingOptionId
          ? shippingOptionPrices[selectedShippingOptionId] || 0
          : 0,

        // Buy Now specific properties - set when isBuyNow is true
        // This ensures consistency with GetShippingOptions endpoint
        IsBuyNow: isBuyNow || undefined,
        CartName: isBuyNow ? `GooglePay_BuyNow_${productCode}` : undefined,
        ProductCode: isBuyNow ? productCode : undefined,

        // Include tender data lines if available (empty for Buy Now)
        TenderDataLines: sourceCheckoutData?.tenderDataLines || [],
      };

      // Use the appropriate method based on Buy Now or regular checkout
      if (isBuyNow) {
        createBuyNowGooglePayOrder(googlePayOrder, {
          onError: () => {
            // Clear loading state only on error
            console.log('GooglePay Buy Now: Order creation failed');
            globalProcessingActive = false;
            setIsProcessingPayment(false);
            if (processingTimeoutRef.current) {
              clearTimeout(processingTimeoutRef.current);
            }
          },
          // Note: We don't use onSettled here to keep loading state active
          // until redirect happens or 3DS is required
        });
      } else {
        createGooglePayOrder(googlePayOrder, {
          onError: () => {
            // Clear loading state only on error
            console.log('GooglePay: Order creation failed');
            globalProcessingActive = false;
            setIsProcessingPayment(false);
            if (processingTimeoutRef.current) {
              clearTimeout(processingTimeoutRef.current);
            }
          },
          // Note: We don't use onSettled here to keep loading state active
          // until redirect happens or 3DS is required
        });
      }
    } catch (error: any) {
      console.error('Google Pay Error:', error);

      // Clear loading state on error
      globalProcessingActive = false;
      setIsProcessingPayment(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }

      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              {error.message || 'Payment processing failed'}
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    }
  };

  // Fetch delivery options only when country changes or on mount — NOT on amount changes.
  // This prevents the GetShippingOptions call from firing every time checkout amounts update.
  useEffect(() => {
    const loadDeliveryOptions = async () => {
      const defaultCountry = countryCode || 'IE';
      console.log('GooglePay: Delivery options effect - fetching for country:', defaultCountry);
      const response = await fetchDeliveryOptions(defaultCountry, isBuyNow, productCode);
      cachedDeliveryOptionsRef.current = response;
    };
    loadDeliveryOptions();
  }, [countryCode, isBuyNow, productCode, fetchDeliveryOptions]);

  // Initialize payment request with current amount and shipping options
  useEffect(() => {
    const initializePaymentRequest = async () => {
      // Check if another instance is already initializing
      if (globalPaymentRequestActive && !isInitializingRef.current) {
        console.log('GooglePay: Another PaymentRequest is already active, skipping initialization');
        return;
      }

      // Check if component is still mounted
      if (!componentMountedRef.current) {
        console.log('GooglePay: Component unmounted, skipping initialization');
        return;
      }

      // For initial payment request, use the total amount directly
      // When gift cards/loyalty points are applied, the total already reflects the final amount
      let initialAmount = 0;
      if (isBuyNow && productPrice) {
        initialAmount = parsePrice(productPrice);
      } else if (checkoutData?.amounts?.total) {
        // Use the total amount directly, ensuring it's never negative
        initialAmount = Math.max(0, parsePrice(checkoutData.amounts.total));
      }

      const currentCurrency = googlePaySettings.currencyCode || 'EUR';

      // For basket page, we need to update even during refresh to ensure Google Pay shows correct amount
      // Only skip updates during refresh for checkout pages to avoid flickering
      const isBasketPage = window.location.pathname === '/checkout/basket';

      // Debug logging
      console.log('GooglePay: Initializing payment request', {
        isBuyNow,
        productPrice,
        productOriginalPrice,
        rawTotal: checkoutData?.amounts?.total,
        initialAmount,
        currentCurrency,
        countryCode,
        merchantId: googlePaySettings.merchantId,
        merchantName: googlePaySettings.merchantName,
        checkoutData: !!checkoutData,
        isRefreshing,
        isBasketPage,
        timestamp: new Date().toISOString(),
        amounts: checkoutData?.amounts,
        subtotal: checkoutData?.amounts?.subtotal,
        discount: checkoutData?.amounts?.discount,
        shipping: checkoutData?.amounts?.shipping,
        giftCard: checkoutData?.amounts?.giftCard,
        loyaltyCard: checkoutData?.amounts?.loyaltyCard,
        baseAmount: initialAmount,
        globalPaymentRequestActive,
        isInitializingRef: isInitializingRef.current,
      });

      if (!isBuyNow && isRefreshing && !isBasketPage) {
        console.log(
          'GooglePay: Skipping payment request update while refreshing cart data (not on basket page)',
        );
        return;
      }

      // For Buy Now mode, ensure we have product price
      if (isBuyNow && !productPrice) {
        console.warn(
          'GooglePay: Buy Now mode requires productPrice, but none provided',
        );
        setPaymentRequest(null);
        return;
      }

      // Ensure amount is valid
      if (initialAmount <= 0) {
        console.warn(
          'GooglePay: Invalid amount, skipping payment request creation',
          { initialAmount, isBuyNow, productPrice },
        );
        setPaymentRequest(null);
        return;
      }

      // Set the global flag to prevent other instances
      globalPaymentRequestActive = true;
      isInitializingRef.current = true;

      try {
        // Use cached delivery options from the separate effect — no network call here.
        const defaultCountry = countryCode || 'IE';
        console.log(
          'GooglePay: Using cached delivery options for country:',
          defaultCountry,
        );

        let shippingOptions: any[] = [];
        let defaultSelectedOptionId: string | undefined;

        try {
          // Read from cache populated by the delivery options effect
          const deliveryOptionsResponse = cachedDeliveryOptionsRef.current;

          if (
            deliveryOptionsResponse &&
            deliveryOptionsResponse.isDeliverySupported &&
            deliveryOptionsResponse.options.length > 0
          ) {
            // Convert to Google Pay format - NO amount field in initial options
            shippingOptions = deliveryOptionsResponse.options.map(option => ({
              id: option.id,
              label: option.label, // Use title as-is from backend
              description: option.description, // Already includes price from backend
            }));

            // Find default option
            const defaultOption =
              deliveryOptionsResponse.options.find(opt => opt.isDefault) ||
              deliveryOptionsResponse.options[0];
            defaultSelectedOptionId = defaultOption?.id;

            // Store current shipping options and prices
            currentShippingOptionsRef.current = deliveryOptionsResponse.options;
            const prices: Record<string, number> = {};
            deliveryOptionsResponse.options.forEach(opt => {
              prices[opt.id] = parseFloat(opt.amount);
            });
            setShippingOptionPrices(prices);
          }
        } catch (shippingError) {
          console.warn(
            'GooglePay: Failed to fetch initial shipping options, continuing without them',
            shippingError,
          );
          // Continue without shipping options - user can still proceed with checkout
        }

        // Debug tokenization settings before creating request
        console.log('GooglePay: Tokenization debug:', {
          gateway: googlePaySettings.gatewayName || 'opayoelavon',
          gatewayMerchantId: googlePaySettings.opayoMerchantId || '',
          hasOpayoMerchantId: !!googlePaySettings.opayoMerchantId,
          opayoMerchantIdType: typeof googlePaySettings.opayoMerchantId,
          merchantId: googlePaySettings.merchantId,
          environment: googlePaySettings.environment,
        });

        // Only use discount code from a previous sheet interaction (ref).
        // On fresh page load the code will be synced from the backend via the SetShippingMethod response.
        const existingDiscountCode = currentCouponCodeRef.current || null;
        if (existingDiscountCode) {
          console.log('GooglePay: Found existing discount code:', existingDiscountCode);
          // Initialize the ref with the existing code
          previousCouponCodeRef.current = existingDiscountCode;
          currentCouponCodeRef.current = existingDiscountCode;
        }

        // Build base request
        const request: google.payments.api.PaymentDataRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['MASTERCARD', 'VISA'],
                billingAddressRequired: true,
                billingAddressParameters: {
                  format: 'FULL',
                  phoneNumberRequired: true,
                },
              },
              tokenizationSpecification: {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                  gateway: googlePaySettings.gatewayName || 'opayoelavon',
                  gatewayMerchantId: googlePaySettings.opayoMerchantId || '',
                },
              },
            },
          ],
          merchantInfo: {
            merchantId: googlePaySettings.merchantId || '',
            merchantName: googlePaySettings.merchantName || 'Eason',
          },
          transactionInfo: {
            totalPriceStatus: 'ESTIMATED',
            totalPriceLabel: 'Total',
            totalPrice: initialAmount.toFixed(2),
            currencyCode: currentCurrency,
            countryCode: countryCode,
            displayItems: buildDisplayItems({
              isBuyNow,
              currentAmount: initialAmount,
              checkoutData: checkoutData || undefined,
              productPrice,
              productOriginalPrice,
              showShipping: false,
            }),
          },
          emailRequired: true,
          shippingAddressRequired: true,
          shippingOptionRequired: true,
          shippingAddressParameters: {
            // Let backend decide which countries are supported
            phoneNumberRequired: true,
          },
          // Always include both SHIPPING_ADDRESS and SHIPPING_OPTION
          // shippingOptionRequired must be true to match SHIPPING_OPTION in callbackIntents,
          // otherwise Google rejects the request with DEVELOPER_ERROR.
          // Shipping options are populated via onPaymentDataChanged when the user enters an address.
          callbackIntents: googlePaySettings.isCouponEntryEnabled
            ? ['SHIPPING_ADDRESS', 'SHIPPING_OPTION', 'OFFER', 'PAYMENT_AUTHORIZATION']
            : ['SHIPPING_ADDRESS', 'SHIPPING_OPTION'],
        };

        // Add existing discount code to the payment request if present and coupon entry is enabled
        if (googlePaySettings.isCouponEntryEnabled && existingDiscountCode) {
          request.offerInfo = {
            offers: [{
              redemptionCode: existingDiscountCode,
              description: 'Applied code',
            }]
          };
          console.log('GooglePay: Added existing discount code to payment request:', existingDiscountCode);
        }

        console.log('Payment Data Request', request);

        // Add pre-fetched shipping options if available; otherwise onPaymentDataChanged will supply them
        if (shippingOptions.length > 0) {
          request.shippingOptionParameters = {
            defaultSelectedOptionId: defaultSelectedOptionId,
            shippingOptions: shippingOptions,
          };
        }

        console.log('GooglePay: Payment request created successfully', {
          ...request,
          totalPriceInRequest: request.transactionInfo.totalPrice,
          hasShippingOptions: shippingOptions.length > 0,
          defaultShippingId: defaultSelectedOptionId,
        });
        
        // Only set payment request if component is still mounted
        if (componentMountedRef.current) {
          setPaymentRequest(request);
          // Mark as initialized after successfully creating payment request
          setTimeout(() => {
            setHasInitialized(true);
          }, 1000); // Give time for the button to fully initialize
        }
      } catch (error) {
        console.error('GooglePay: Error creating payment request', error);
        if (componentMountedRef.current) {
          setPaymentRequest(null);
        }
      } finally {
        // Clear the initialization flag after a short delay to allow the payment UI to fully initialize
        setTimeout(() => {
          if (isInitializingRef.current) {
            globalPaymentRequestActive = false;
            isInitializingRef.current = false;
            console.log('GooglePay: Cleared initialization flag');
          }
        }, 500);
      }
    };

    // Add a small delay before initializing to avoid rapid re-initialization
    const timeoutId = setTimeout(() => {
      initializePaymentRequest();
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    checkoutData?.amounts?.total,
    checkoutData?.amounts?.subtotal,
    checkoutData?.amounts?.discount,
    checkoutData?.amounts?.shipping,
    checkoutData?.amounts?.giftCard,
    checkoutData?.amounts?.loyaltyCard,
    productPrice,
    productOriginalPrice,
    isBuyNow,
    countryCode,
    googlePaySettings,
    isRefreshing,
  ]);

  if (!paymentRequest || (!isBuyNow && isRefreshing)) {
    console.log('GooglePay: Not ready to render button', {
      paymentRequest: !!paymentRequest,
      isRefreshing,
      isBuyNow,
      checkoutDataTotal: checkoutData?.amounts?.total,
      productPrice,
    });
    return (
      <div style={{ opacity: 0.6 }}>
        {!isBuyNow && isRefreshing
          ? 'Updating payment amount...'
          : 'Loading Google Pay...'}
      </div>
    );
  }

  console.log('GooglePayButtonWrapper: Rendering GooglePayButton with:', {
    environment:
      googlePaySettings.environment === 'TEST' ? 'TEST' : 'PRODUCTION',
    paymentRequest: !!paymentRequest,
    merchantId: googlePaySettings.merchantId,
    merchantName: googlePaySettings.merchantName,
  });

  return (
    <>
      {(isPending || isProcessingPayment || isCompletingThreeDSecure) && (
        <Styled.LoadingOverlay>
          <Styled.LoadingContent>
            <LoadingSpinner />
            <Styled.LoadingText aria-live="assertive" role="alert">
              Processing your order...
            </Styled.LoadingText>
          </Styled.LoadingContent>
        </Styled.LoadingOverlay>
      )}

      {isBuyNow && (
        <>
          <div
            style={{
              textAlign: 'center',
              margin: '1rem 0 0.75rem',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                background:
                  'linear-gradient(to right, transparent, #e0e0e0 20%, #e0e0e0 80%, transparent)',
              }}
            ></div>
            <span
              style={{
                background: '#fff',
                padding: '0 1rem',
                color: 'rgb(113,118,128)',
                fontSize: '0.85rem',
                position: 'relative',
              }}
            >
              or
            </span>
          </div>
        </>
      )}
      <div
        key={`googlepay-${checkoutData?.amounts?.total || 0}`}
        style={{
          borderRadius:'0',
          overflow: 'hidden',
          boxShadow: 'none',
          height: 'auto',
          width: '100%',
          border: 'none',
          position: 'relative',
        }}
      >
        <GooglePayButton
          environment={
            googlePaySettings.environment === 'TEST' ? 'TEST' : 'PRODUCTION'
          }
          buttonType={isBuyNow ? 'buy' : 'plain'}
          buttonColor="black"
          buttonSizeMode="fill"
          style={{ width: '100%', height: isBuyNow ? '44px' : 'auto' }}
          paymentRequest={paymentRequest}
          onLoadPaymentData={paymentData => {
            console.log('Load payment data', paymentData);
            // handleGooglePaySuccess now has its own duplicate protection
            handleGooglePaySuccess(paymentData as GooglePayPaymentData);
          }}
          onError={error => {
            console.error('GooglePay button onError:', {
              error,
              errorCode: (error as any)?.statusCode,
              errorMessage: (error as any)?.statusMessage,
              fullError: JSON.stringify(error, null, 2),
              isBuyNow,
              hasPaymentRequest: !!paymentRequest,
              environment: googlePaySettings.environment,
            });
            // Add a small delay to ensure we're not catching initialization errors
            setTimeout(() => {
              handleGooglePayError(error);
            }, 100);
          }}
          onPaymentDataChanged={onPaymentDataChanged}
          onPaymentAuthorized={
            googlePaySettings.isCouponEntryEnabled
              ? paymentData => {
                  console.log('GooglePay: Payment authorized', paymentData);
                  // Required for OFFER intent to work
                  // Just approve - actual payment happens in onLoadPaymentData
                  return Promise.resolve({
                    transactionState: 'SUCCESS' as google.payments.api.TransactionState,
                  });
                }
              : undefined
          }
          onReadyToPayChange={result => {
            console.log('Ready to pay change', result);
            if (result.isReadyToPay) {
              console.log('Google Pay is ready');
            } else if (result.hasOwnProperty('isReadyToPay') && !result.isReadyToPay) {
              // Google Pay is explicitly not available
              console.warn('Google Pay is not available in this browser/environment');
            }
          }}
        />
      </div>

      {threeDSecureUrl && (
        <ThreeDSecureModal
          url={threeDSecureUrl}
          onComplete={handleThreeDSecureComplete}
          onClose={() => {
            // User closed modal without completing
            handleThreeDSecureComplete(false);
          }}
        />
      )}
    </>
  );
};

export default GooglePayButtonWrapper;
